import { createElement, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { ApexOptions } from "apexcharts";
import { t, useI18n, type I18nChildContext } from "@bookingcom/lingojs-react";
import type { DiscountedBookingsChartProps } from "./DiscountedBookingsChart";
import { DiscountedBookingsTooltip } from "./DiscountedBookingsTooltip";
import { CHART_DATE_TICK_AMOUNT, formatChartDate, formatChartDateAxisLabel } from "../../../utils/chart";
import { AIRLINE_SERIES_COLORS } from "../ChartConstants";

const buildSingleSeriesOptions = (categories: number[], i18n: I18nChildContext): ApexOptions => {
  return {
    chart: {
      type: "line",
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    colors: AIRLINE_SERIES_COLORS,
    stroke: {
      curve: "straight",
      width: 2,
    },
    markers: {
      size: 5,
      colors: ["var(--bui_color_action_foreground)"],
      strokeColors: "var(--bui_color_on_action_background)",
      strokeWidth: 2,
      hover: { size: 7 },
    },
    xaxis: {
      type: "category",
      categories,
      tickPlacement: "on",
      tickAmount: CHART_DATE_TICK_AMOUNT,
      title: {
        text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_one_xaxis")),
        offsetY: 24,
      },
      labels: {
        rotate: 0,
        rotateAlways: false,
        formatter: formatChartDateAxisLabel,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => Math.round(val).toString(),
        padding: 60,
      },
    },
    grid: {
      borderColor: "var(--bui_color_border_alt)",
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 24, left: 80, right: 40, bottom: 20 },
    },
    tooltip: {
      intersect: true,
      shared: false,
      x: { format: "d MMM yyyy" },
      y: {
        title: {
          formatter: () => `${i18n.trans(t("flights_airline_portal_realtime_performance_metric_one_yaxis"))}:`,
        },
        formatter: (val: number) => Math.round(val).toString(),
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: "top",
      horizontalAlign: "right",
      markers: { size: 7, shape: "circle" },
      offsetY: 0,
      onItemClick: { toggleDataSeries: false },
    },
  };
};

const buildMultiSeriesOptions = (categories: number[], i18n: I18nChildContext): ApexOptions => {
  return {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    colors: AIRLINE_SERIES_COLORS,
    plotOptions: {
      bar: {
        columnWidth: `${Math.min(90, Math.max(25, categories.length * 3))}%`,
        borderRadius: 0,
      },
    },

    xaxis: {
      type: "category",
      categories,
      tickPlacement: "on",
      tickAmount: CHART_DATE_TICK_AMOUNT,
      title: {
        text: i18n.trans(t("flights_airline_portal_realtime_performance_metric_one_xaxis")),
        offsetY: 24,
      },
      labels: {
        rotate: 0,
        rotateAlways: false,
        formatter: formatChartDateAxisLabel,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => Math.round(val).toString(),
        padding: 60,
      },
    },
    grid: {
      borderColor: "var(--bui_color_border_alt)",
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, left: 80, right: 40, bottom: 20 },
    },
    tooltip: {
      intersect: true,
      shared: false,
      x: { format: "d MMM yyyy" },
      custom: ({ series: seriesValues, dataPointIndex, w }) => {
        const rows = w.globals.seriesNames.map((name: string, index: number) => ({
          name,
          value: Math.round(seriesValues[index]?.[dataPointIndex] ?? 0).toString(),
          color: w.globals.colors[index],
        }));
        const total = seriesValues.reduce((sum: number, values: number[]) => sum + (values[dataPointIndex] ?? 0), 0);
        return renderToStaticMarkup(
          createElement(DiscountedBookingsTooltip, {
            title: formatChartDate(categories[dataPointIndex]),
            rows,
            totalLabel: i18n.trans(t("flights_airline_portal_group_view_discounts_daily_total")),
            totalValue: Math.round(total).toString(),
          })
        );
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: "top",
      horizontalAlign: "right",
      markers: { size: 7, shape: "circle" },
      offsetY: 0,
      onItemClick: { toggleDataSeries: false },
    },
  };
};

export const useDiscountedBookingsChartController = ({ series }: DiscountedBookingsChartProps) => {
  const i18n = useI18n();

  const isSingleSeries = series.length <= 1;
  const buildOptions = isSingleSeries ? buildSingleSeriesOptions : buildMultiSeriesOptions;

  const totalBookings = useMemo(
    () => Math.round(series.reduce((sum, { metric }) => sum + (metric.total?.value ?? 0), 0)),
    [series]
  );

  const categories = useMemo(() => {
    const times = new Set<number>();
    series.forEach(({ metric }) =>
      metric.timeSeries.dataPoints.forEach((dp) => times.add(new Date(dp.time).getTime()))
    );
    return [...times].sort((a, b) => a - b);
  }, [series]);

  const chartSeries: ApexAxisChartSeries = useMemo(() => {
    return series.map(({ airlineCode, airlineName, metric }) => {
      const valueByTime = new Map(metric.timeSeries.dataPoints.map((dp) => [new Date(dp.time).getTime(), dp.value]));

      return {
        name: isSingleSeries
          ? i18n.trans(t("flights_airline_portal_realtime_performance_metric_one_data_key"))
          : `${airlineName ?? ""} (${airlineCode ?? ""})`,
        data: categories.map((time) => valueByTime.get(time) ?? 0),
      };
    });
  }, [series, isSingleSeries, categories, i18n]);

  const options: ApexOptions = useMemo(() => buildOptions(categories, i18n), [buildOptions, categories, i18n]);

  return {
    totalBookings,
    options,
    series: chartSeries,
    chartType: isSingleSeries ? ("line" as const) : ("bar" as const),
    isSingleSeries,
  };
};
